import { Grid } from "@mui/material"
import { push } from "connected-react-router"
import { createContext, FC, useCallback, useContext } from "react"
import { useDispatch } from "react-redux"
import { Content } from "@/components/content/Content"
import { useDatabaseSelectCard, useEncryptableDatabasesSource } from "@/components/databases/DatabaseSelectCard"
import HistoricalEncryptionTable from "@/components/obfuscation/HistoricalEncryptionTable"
import { DatabaseResponseDto } from "@/gen/api/backup"
import { OdResourcePageProps } from "@/types/resource"
import { crudGetList, crudGetOne } from "ra-core"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useResourceRegister } from "@/utils/resourceRegister"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"


// 
// controller/context/provider
// 

export interface EncryptionDetailControllerProps {
  fireConfigFileDownload(dbId: number, encryptionId: number): void;
}

const EncryptionDetailContext = createContext<EncryptionDetailControllerProps>({
  fireConfigFileDownload: () => Promise.reject(),
})


export const EncryptionDetailContextProvider: FC<{ value: EncryptionDetailControllerProps }> = ({ children, value }) => {
  return (
    <EncryptionDetailContext.Provider value={value}>
      {children}
    </EncryptionDetailContext.Provider>
  )
}

export const useEncryptionDetailController = (): EncryptionDetailControllerProps => {
  const dispatch = useDispatch()

  const fireConfigFileDownload = useCallback((dbId: number, encryptionId: number) => {
    dispatch(crudGetList('encryption-rec-file', pages(), sorts(), { dbId, encryptionId }))
  }, [dispatch])

  return {
    fireConfigFileDownload,
  }
}

export const useEncryptionDetailContext = () => useContext(EncryptionDetailContext);


// 
// view components: main/cater/body
// 


const EncryptionDetail: FC<OdResourcePageProps & { id?: string}> = (props) => {
  useResourceRegister({ name: 'encryption-historic-recs' })
  useResourceRegister({ name: 'encryption-rec-file' })
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '') 
  const dispatch = useDispatch()

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/encryption/list/$0/show`, db.databaseId || 0, db.name)))
  }, [dispatch])

  const pageCtrls = useEncryptionDetailController()
  return (
    <EncryptionDetailContextProvider value={pageCtrls}>
      <EncryptedDatabasesContextProvider>
        <EncryptionDetailBody onSwitchDatabase={handleSwitchDatabase} {...{id}} {...rest} />
      </EncryptedDatabasesContextProvider>
    </EncryptionDetailContextProvider>
  )
}

export interface EncryptionDetailBodyProps extends OdResourcePageProps {
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  id: number;
}

export const EncryptionDetailBody: FC<EncryptionDetailBodyProps> = ({
  id,
  onSwitchDatabase,
  options,
  resource,
}) => {
  const DatabaseSelectCard = useDatabaseSelectCard(useEncryptableDatabasesSource)
  
  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      onSwitchDatabase(chosenDb)
    }
  }, [onSwitchDatabase])

  return (
    <Content 
      pageName="Encryption Detail" 
      resourceName="Detail" 
      breadcrumbs={[...(options?.breadcrumbs || []), { label: 'Encryption Jobs', path: '/encryption/list' }]}
    >
      <Grid container spacing={4}>
        <Grid
          item
          md={6}
          xs={12}
        >
          <DatabaseSelectCard preSelectDbId={id} onChange={handleChange} />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <HistoricalEncryptionTable databaseId={id} />
        </Grid>
      </Grid>
    </Content>
  )
}



export default EncryptionDetail;
